import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, ChevronDown, Info, AlertCircle, ArrowLeft, Plus, Eye, Edit2, X, Tag } from 'lucide-react';
import { DistributorChannelStatCards } from './components/DistributorChannelStatCards';
import { DistributorChannelFilters } from './components/DistributorChannelFilters';
import type { ChannelFilterState } from './components/DistributorChannelFilters';
import { AddEditDistributorChannelModal, type ChannelFormData } from './components/AddEditDistributorChannelModal';
import {
  getDistributorChannelsList,
  createDistributorChannel,
  updateDistributorChannel,
  type ApiDistributorChannel,
} from '../../../api/distributorChannel';
import { TableLoader } from '../../../components/Common/Loader';
import CSVButton from '../../../components/Common/CSVButton';
import { exportToCSV } from '../../../utils/csvExport';

export const DistributorChannel: React.FC = () => {
  const [channels, setChannels] = useState<ApiDistributorChannel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [entriesCount, setEntriesCount] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [tableSearch, setTableSearch] = useState<string>('');

  // Modals state
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState<boolean>(false);
  const [editingChannel, setEditingChannel] = useState<ApiDistributorChannel | null>(null);
  const [viewingChannel, setViewingChannel] = useState<ApiDistributorChannel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string | null>(null);

  // Filters State
  const [filters, setFilters] = useState<ChannelFilterState>({
    distributorType: '',
    searchQuery: '',
    status: '',
  });

  const fetchChannels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getDistributorChannelsList();

      let rawList: ApiDistributorChannel[] = [];
      if (res && Array.isArray(res.data)) {
        rawList = res.data;
      } else if (Array.isArray(res)) {
        rawList = res;
      } else if (res && typeof res === 'object' && Array.isArray((res as any).channels)) {
        rawList = (res as any).channels;
      }

      setChannels(rawList);
    } catch (err: any) {
      console.error('Error fetching distributor channels:', err);
      setError(err?.message || 'Failed to load distributor channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Clear success toast
  useEffect(() => {
    if (actionSuccessMsg) {
      const timer = setTimeout(() => setActionSuccessMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccessMsg]);

  // Stats calculation
  const totalCount = channels.length;
  const activeCount = useMemo(() => channels.length, [channels]);

  // Filtered dataset
  const filteredChannels = useMemo(() => {
    return channels.filter((item) => {
      const nameMatch = String(item.name || '').toLowerCase();
      const descMatch = String(item.description || '').toLowerCase();
      const sapMatch = String(item.sapTypeId ?? '').toLowerCase();

      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        if (!nameMatch.includes(q) && !descMatch.includes(q) && !sapMatch.includes(q)) return false;
      }
      if (tableSearch) {
        const q = tableSearch.toLowerCase();
        if (!nameMatch.includes(q) && !descMatch.includes(q) && !sapMatch.includes(q)) return false;
      }
      return true;
    });
  }, [channels, filters, tableSearch]);

  // Pagination calculation
  const totalEntries = filteredChannels.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / entriesCount));
  const paginatedChannels = useMemo(() => {
    const start = (currentPage - 1) * entriesCount;
    return filteredChannels.slice(start, start + entriesCount);
  }, [filteredChannels, currentPage, entriesCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, tableSearch, entriesCount]);

  // Handle Save (Add or Edit)
  const handleSaveChannel = async (formData: ChannelFormData) => {
    try {
      setIsSubmitting(true);
      if (formData.id) {
        // Edit existing
        await updateDistributorChannel(formData.id, {
          name: formData.name,
          description: formData.description,
          sapTypeId: formData.sapTypeId,
        });
        setActionSuccessMsg(`Distributor Channel "${formData.name}" updated successfully!`);
      } else {
        // Create new
        await createDistributorChannel({
          name: formData.name,
          description: formData.description,
          sapTypeId: formData.sapTypeId,
        });
        setActionSuccessMsg(`Distributor Channel "${formData.name}" created successfully!`);
      }
      setIsAddEditModalOpen(false);
      setEditingChannel(null);
      await fetchChannels();
    } catch (err: any) {
      console.error('Error saving distributor channel:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportExcel = () => {
    if (filteredChannels.length === 0) return;
    const headers = ['ID', 'Channel Name', 'Description', 'SAP Type ID', 'Created At', 'Updated At'];
    const rows = filteredChannels.map((r) => [
      r.id,
      r.name || '',
      r.description || '',
      r.sapTypeId ?? '-',
      r.createdAt ? new Date(r.createdAt).toLocaleString() : '-',
      r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '-',
    ]);
    exportToCSV('distributor_channels', headers, rows);
  };

  return (
    <div className="flex flex-col gap-6 pb-6 select-none">
      {/* Toast Notification */}
      {actionSuccessMsg && (
        <div className="bg-emerald-500 text-white px-4 py-3 rounded-2xl shadow-lg flex items-center justify-between text-xs md:text-sm font-semibold animate-fadeIn">
          <span>{actionSuccessMsg}</span>
          <button onClick={() => setActionSuccessMsg(null)} className="hover:opacity-80 cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header Navigation & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
              Distributor Channels
            </h1>
            <p className="text-xs font-semibold text-slate-400 mt-0.5">
              Manage distribution channels for General Trade, Modern Trade, and sales orgs
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <CSVButton onClick={handleExportExcel} />

          <button
            onClick={() => {
              setEditingChannel(null);
              setIsAddEditModalOpen(true);
            }}
            className="px-4 py-1.5 bg-[#C1272D] hover:bg-[#A81E23] text-white text-xs font-bold rounded-xl shadow-md shadow-[#C1272D]/20 flex items-center gap-1.5 cursor-pointer transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>Add Channel</span>
          </button>
        </div>
      </div>

      {/* Filters Toolbar */}
      <DistributorChannelFilters
        filters={filters}
        onFilterChange={setFilters}
        onReset={() => setFilters({ distributorType: '', searchQuery: '', status: '' })}
      />

      {/* Stat Cards */}
      <DistributorChannelStatCards
        totalDistributors={totalCount}
        activeDistributors={activeCount}
        inactiveDistributors={0}
      />

      {/* Channels List Table Card */}
      <div className="bg-white rounded-3xl border border-[#ECECF2] shadow-sm overflow-hidden flex flex-col">
        {/* Header & Controls */}
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white">
          <h3 className="text-base font-bold text-slate-800">Channels List</h3>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <span>Show</span>
              <div className="relative">
                <select
                  value={entriesCount}
                  onChange={(e) => setEntriesCount(Number(e.target.value))}
                  className="appearance-none bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 pr-7 text-xs font-bold text-slate-700 focus:outline-none cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
              </div>
              <span>entries</span>
            </div>

            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search channels..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D]"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto min-h-[300px] relative">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <TableLoader />
            </div>
          ) : error ? (
            <div className="py-16 text-center text-rose-500 text-xs font-semibold flex flex-col items-center gap-3">
              <AlertCircle className="w-8 h-8 text-rose-400" />
              <p>{error}</p>
              <button
                onClick={fetchChannels}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                Retry
              </button>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#FFFBEB] border-b border-amber-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                  <th className="py-3.5 px-6">ID</th>
                  <th className="py-3.5 px-6">Channel Name</th>
                  <th className="py-3.5 px-6">Description</th>
                  <th className="py-3.5 px-6 text-center">SAP Type ID</th>
                  <th className="py-3.5 px-6">Created At</th>
                  <th className="py-3.5 px-6">Updated At</th>
                  <th className="py-3.5 px-6 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {paginatedChannels.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-6 font-medium text-slate-400">#{item.id}</td>
                    <td className="py-4 px-6 font-bold text-slate-900">{item.name || '-'}</td>
                    <td className="py-4 px-6 font-medium text-slate-600 max-w-xs">{item.description || '-'}</td>
                    <td className="py-4 px-6 text-center font-mono font-bold text-slate-700">
                      {item.sapTypeId !== undefined && item.sapTypeId !== null ? (
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-800 rounded-md text-[11px]">
                          {item.sapTypeId}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-4 px-6 font-medium text-slate-500 whitespace-nowrap">
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="py-4 px-6 font-medium text-slate-500 whitespace-nowrap">
                      {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewingChannel(item)}
                          className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingChannel(item);
                            setIsAddEditModalOpen(true);
                          }}
                          className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {paginatedChannels.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400 font-medium">
                      No distributor channels found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        {!loading && !error && (
          <div className="p-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white text-xs font-semibold text-slate-500">
            <span>
              Showing {totalEntries === 0 ? 0 : (currentPage - 1) * entriesCount + 1} to{' '}
              {Math.min(currentPage * entriesCount, totalEntries)} of {totalEntries} entries
            </span>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 font-bold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))
                .map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg font-bold flex items-center justify-center transition-all cursor-pointer ${
                      currentPage === pageNum
                        ? 'bg-[#C1272D] text-white shadow-xs'
                        : 'border border-slate-200 hover:bg-slate-50 text-slate-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-[#C1272D] font-bold transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="bg-blue-50/70 border border-blue-100 rounded-2xl p-4 flex items-center gap-3 text-xs text-blue-900 font-medium">
        <Info className="w-5 h-5 text-blue-600 shrink-0" />
        <span>
          <strong className="font-bold text-blue-950">Note:</strong> Distributor channels map to General Trade, Modern Trade, and custom SAP type categories.
        </span>
      </div>

      {/* Add / Edit Channel Modal */}
      <AddEditDistributorChannelModal
        isOpen={isAddEditModalOpen}
        onClose={() => {
          setIsAddEditModalOpen(false);
          setEditingChannel(null);
        }}
        onSave={handleSaveChannel}
        initialData={editingChannel}
        isSubmitting={isSubmitting}
      />

      {/* View Channel Details Modal */}
      {viewingChannel && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-slate-100 space-y-6 animate-fadeIn my-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-slate-100 text-[#C1272D] rounded-2xl">
                  <Tag className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-slate-800 tracking-tight">
                    {viewingChannel.name || 'Channel Details'}
                  </h3>
                  <p className="text-xs font-mono font-medium text-slate-400">
                    Channel ID: {viewingChannel.id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewingChannel(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-4 text-xs">
              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">
                  Channel Name
                </span>
                <p className="text-sm font-bold text-slate-900">{viewingChannel.name || '—'}</p>
              </div>

              <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">
                  Description
                </span>
                <p className="text-xs font-medium text-slate-700 leading-relaxed">
                  {viewingChannel.description || 'No description provided.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">
                    SAP Type ID
                  </span>
                  <p className="text-sm font-mono font-bold text-slate-800">
                    {viewingChannel.sapTypeId !== undefined && viewingChannel.sapTypeId !== null ? viewingChannel.sapTypeId : '—'}
                  </p>
                </div>

                <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-1">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">
                    Created At
                  </span>
                  <p className="text-xs font-medium text-slate-700">
                    {viewingChannel.createdAt ? new Date(viewingChannel.createdAt).toLocaleString() : '—'}
                  </p>
                </div>
              </div>

              {viewingChannel.updatedAt && (
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-[11px] text-slate-500 font-medium">
                  Last Updated: <strong className="text-slate-800">{new Date(viewingChannel.updatedAt).toLocaleString()}</strong>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end pt-4 border-t border-slate-100">
              <button
                onClick={() => setViewingChannel(null)}
                className="px-6 py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl cursor-pointer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistributorChannel;
